import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Select.css';
import useOuterClick from '../../../customHooks/useOuterClick';
import BiaIcon from '../BiaIcon/BiaIcon';
import type { SelectOption } from '~/types/roles';
import Flex from '~/components/Flex';




export interface BiaSelectProps {
  options: SelectOption[];
  placeHolder?: string;
  value: string | number | undefined;
  onChange: (optionId: string | number) => void;
  className?: string;
  labelClass?: string;
  dropDownClass?: string;
  labelStyle?: React.CSSProperties;
  tooltip?: string;
  multiple?: boolean;
  search?: boolean;
  groupBy?: string;
}

const BiaSelect: React.FC<BiaSelectProps> = ({
  options,
  placeHolder,
  value,
  onChange,
  className = '',
  labelClass = '',
  dropDownClass = '',
  labelStyle = {},
  tooltip = '',
  multiple = false,
  search = false,
  groupBy,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const clickedOutSide = useOuterClick(wrapperRef as React.RefObject<HTMLElement>);

  useEffect(() => {
    if (clickedOutSide) {
      setOpen(false);
    }
  }, [clickedOutSide]);

  // Update dropdown position when opening - calculate position first, then show
  useEffect(() => {
    if (open && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
      // Delay visibility to allow position to be set first
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
      // Clear position after animation completes
      if (!open) {
        const timer = setTimeout(() => setDropdownPosition(null), 300);
        return () => clearTimeout(timer);
      }
    }
  }, [open]);

  // Close dropdown when clicking outside the portal
  useEffect(() => {
    if (!open) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleChange = useCallback((optionId: string | number) => {
    setOpen(false);
    onChange(optionId);
  }, [onChange]);

  const displayedvalue = value && options.find(
    option => option.id == value || option.name == value,
  );

  const dropdownContent = dropdownPosition ? (
    <ul 
      ref={dropdownRef}
      className={`${dropDownClass} p-1 bia-select-dropdown-portal ${isVisible ? 'active' : ''}`}
      style={{
        position: 'absolute',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
    >
      {options.map(option => {
        return (
          <li
            key={option.id}
            className="hover-secondary cursor-pointer"
            onClick={() => handleChange(option.id)}
          >
            {option.name}
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <div
      className={`bia-select ${className} ${value ? 'active' : ''}`}
      ref={wrapperRef}
    >
      {placeHolder && (
        <label style={labelStyle} className={labelClass}>
          <span>
            {placeHolder}
          </span>
          {tooltip.length > 0 && (
            <span>
              ({tooltip})
            </span>
          )}
        </label>
      )}
      <Flex 
        className="bia-select-button" 
        onClick={() => setOpen(!open)}
   
      >
        <span className="bia-select-value">
          {displayedvalue 
            ? (displayedvalue.name) 
            : placeHolder
          }
        </span>
        <BiaIcon 
          icon='chevron-down' 
          rotate={open ? 180 : 0}
          className={labelClass}
        />
      </Flex>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default BiaSelect;
